import { useEffect, useState } from "react";
import type { SceneNumberingMode } from "@supermanus/document-model";
import { handleFieldEnterKey } from "./fieldKeyboard";
import type { EditorSettings, ScriptColumnOrder } from "./editorSettings";

export interface SettingsPanelProps {
  open: boolean;
  onClose: () => void;
  displayName: string;
  onRenameDisplayName: (name: string) => void;
  settings: EditorSettings;
  onSettingsChange: (patch: Partial<EditorSettings>) => void;
  onSaveUserPreferences?: () => void;
  onResetUserPreferences?: () => void;
  useSeasons?: boolean;
  onSetUseSeasons?: (enabled: boolean) => void;
  sceneNumberingMode?: SceneNumberingMode;
  onChangeSceneNumberingMode?: (mode: SceneNumberingMode) => void;
  episodeNumberingMode?: SceneNumberingMode;
  onChangeEpisodeNumberingMode?: (mode: SceneNumberingMode) => void;
  readOnly?: boolean;
  onShowResolvedComments?: () => void;
  resolvedCommentCount?: number;
}

export function SettingsPanel({
  open,
  onClose,
  displayName,
  onRenameDisplayName,
  settings,
  onSettingsChange,
  onSaveUserPreferences,
  onResetUserPreferences,
  useSeasons,
  onSetUseSeasons,
  sceneNumberingMode,
  onChangeSceneNumberingMode,
  episodeNumberingMode,
  onChangeEpisodeNumberingMode,
  readOnly,
  onShowResolvedComments,
  resolvedCommentCount = 0,
}: SettingsPanelProps) {
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!savedFlash) return;
    const t = window.setTimeout(() => setSavedFlash(false), 2000);
    return () => window.clearTimeout(t);
  }, [savedFlash]);

  if (!open) return null;

  return (
    <div className="sm-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="sm-modal sm-settings-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sm-settings-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sm-modal-head">
          <h2 id="sm-settings-title">Indstillinger</h2>
          <button type="button" onClick={onClose}>
            Luk
          </button>
        </header>
        <div className="sm-settings-body">
          <section>
            <h3>Bruger</h3>
            <label className="sm-setting sm-setting-text">
              <span>Dit navn</span>
              <input
                type="text"
                value={displayName}
                placeholder="Dit navn"
                onChange={(e) => onRenameDisplayName(e.target.value)}
                onBlur={(e) => {
                  const next = e.target.value.trim() || "Forfatter";
                  if (next !== displayName) onRenameDisplayName(next);
                }}
                onKeyDown={handleFieldEnterKey}
              />
            </label>
          </section>

          <section>
            <h3>Manus</h3>
            <label className="sm-setting">
              <input
                type="checkbox"
                checked={settings.clickSelectsCell}
                onChange={(e) =>
                  onSettingsChange({
                    clickSelectsCell: e.target.checked,
                  })
                }
              />
              <span>Første klik markerer cellen</span>
            </label>
            <p className="sm-setting-hint">
              Formatknapper og Ctrl/⌘+B påvirker al teksten, mens cellen er
              markeret. I en tom celle kan du skrive med det samme. Andet klik
              i en celle med tekst sætter cursoren. Shift-klik markerer cellen,
              uanset om indstillingen er slået til. Gælder manus og noter.
            </p>
            <label className="sm-setting">
              <input
                type="checkbox"
                checked={settings.newRowOnDoubleReturn}
                onChange={(e) =>
                  onSettingsChange({
                    newRowOnDoubleReturn: e.target.checked,
                  })
                }
              />
              <span>Dobbelt Return opretter ny række</span>
            </label>
            <label className="sm-setting">
              <input
                type="checkbox"
                checked={settings.printColumnRule}
                onChange={(e) =>
                  onSettingsChange({ printColumnRule: e.target.checked })
                }
              />
              <span>Lodret streg mellem billed- og lydside</span>
            </label>
            <div className="sm-setting sm-setting-segment">
              <span className="sm-setting-segment-label">
                Vandrette streger
              </span>
              <div
                className="sm-segmented"
                role="radiogroup"
                aria-label="Vandrette streger mellem rækker"
              >
                {(
                  [
                    ["all", "Alle"],
                    ["none", "Ingen"],
                    ["custom", "Egne"],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={settings.rowRuleMode === value}
                    className={
                      settings.rowRuleMode === value ? "is-active" : undefined
                    }
                    onClick={() => onSettingsChange({ rowRuleMode: value })}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <label className="sm-setting">
              <input
                type="checkbox"
                checked={settings.rowRuleHitInGutter}
                onChange={(e) =>
                  onSettingsChange({
                    rowRuleHitInGutter: e.target.checked,
                  })
                }
              />
              <span>Streg-hover kun mellem kolonner</span>
            </label>
            <div className="sm-setting sm-setting-segment">
              <span className="sm-setting-segment-label">Kolonner</span>
              <div
                className="sm-segmented"
                role="radiogroup"
                aria-label="Kolonnevisning"
              >
                {(
                  [
                    ["visual-audio", "Billede · Lyd"],
                    ["audio-visual", "Lyd · Billede"],
                    ["audio-only", "Kun lyd"],
                  ] as const
                ).map(([value, label]: readonly [ScriptColumnOrder, string]) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={settings.scriptColumnOrder === value}
                    className={
                      settings.scriptColumnOrder === value
                        ? "is-active"
                        : undefined
                    }
                    onClick={() =>
                      onSettingsChange({ scriptColumnOrder: value })
                    }
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <label className="sm-setting">
              <input
                type="checkbox"
                checked={settings.showSetupNumbers}
                onChange={(e) =>
                  onSettingsChange({ showSetupNumbers: e.target.checked })
                }
              />
              <span>Indstillingsnumre</span>
            </label>
            <label className="sm-setting">
              <input
                type="checkbox"
                checked={settings.showCameraNotes}
                onChange={(e) =>
                  onSettingsChange({ showCameraNotes: e.target.checked })
                }
              />
              <span>Kameranoter</span>
            </label>
            <label className="sm-setting">
              <input
                type="checkbox"
                checked={settings.showPosition}
                onChange={(e) =>
                  onSettingsChange({ showPosition: e.target.checked })
                }
              />
              <span>Position</span>
            </label>
            <label className="sm-setting">
              <input
                type="checkbox"
                checked={settings.showComments}
                onChange={(e) =>
                  onSettingsChange({ showComments: e.target.checked })
                }
              />
              <span>Kommentarer</span>
            </label>
            {onShowResolvedComments ? (
              <button
                type="button"
                className="sm-resolved-link"
                onClick={() => {
                  onShowResolvedComments();
                  onClose();
                }}
              >
                Løste kommentarer
                {resolvedCommentCount > 0 ? ` (${resolvedCommentCount})` : ""}
              </button>
            ) : null}
            <label className="sm-setting">
              <input
                type="checkbox"
                checked={settings.confirmDeleteEmptyPair}
                onChange={(e) =>
                  onSettingsChange({
                    confirmDeleteEmptyPair: e.target.checked,
                  })
                }
              />
              <span>Bekræft sletning af tom række</span>
            </label>
            <label className="sm-setting">
              <input
                type="checkbox"
                checked={settings.viewOnly}
                onChange={(e) =>
                  onSettingsChange({ viewOnly: e.target.checked })
                }
              />
              <span>Kun visning (ingen redigering eller sletning)</span>
            </label>
            <label className="sm-setting sm-setting-color">
              <span>Sceneoverskrift-farve</span>
              <input
                type="color"
                value={settings.defaultSceneHeaderBg}
                onChange={(e) =>
                  onSettingsChange({ defaultSceneHeaderBg: e.target.value })
                }
              />
            </label>
          </section>

          <section>
            <h3>Zoom</h3>
            <div className="sm-setting sm-setting-zoom">
              <span>Episode-zoom</span>
              <div className="sm-zoom-controls">
                <button
                  type="button"
                  disabled={settings.episodeZoom <= 0.75}
                  onClick={() =>
                    onSettingsChange({
                      episodeZoom:
                        Math.round((settings.episodeZoom - 0.1) * 10) / 10,
                    })
                  }
                >
                  −
                </button>
                <button
                  type="button"
                  onClick={() => onSettingsChange({ episodeZoom: 1 })}
                >
                  {Math.round(settings.episodeZoom * 100)}%
                </button>
                <button
                  type="button"
                  disabled={settings.episodeZoom >= 1.5}
                  onClick={() =>
                    onSettingsChange({
                      episodeZoom:
                        Math.round((settings.episodeZoom + 0.1) * 10) / 10,
                    })
                  }
                >
                  +
                </button>
              </div>
            </div>
          </section>

          <section>
            <h3>Varighed</h3>
            <label className="sm-setting">
              <input
                type="checkbox"
                checked={settings.durationShowHours}
                onChange={(e) =>
                  onSettingsChange({ durationShowHours: e.target.checked })
                }
              />
              <span>Vis timer</span>
            </label>
            <label className="sm-setting">
              <input
                type="checkbox"
                checked={settings.durationShowFrames}
                onChange={(e) =>
                  onSettingsChange({ durationShowFrames: e.target.checked })
                }
              />
              <span>Vis frames</span>
            </label>
            {settings.durationShowFrames ? (
              <label className="sm-setting sm-setting-fps">
                <span>fps</span>
                <input
                  type="number"
                  min={1}
                  max={120}
                  value={settings.durationFps}
                  onChange={(e) =>
                    onSettingsChange({
                      durationFps: Math.max(
                        1,
                        Math.min(120, Number(e.target.value) || 25),
                      ),
                    })
                  }
                  onKeyDown={handleFieldEnterKey}
                />
              </label>
            ) : null}
          </section>

          <section>
            <h3>Eksport</h3>
            <label className="sm-setting">
              <input
                type="checkbox"
                checked={settings.autoExportRtf}
                onChange={(e) =>
                  onSettingsChange({ autoExportRtf: e.target.checked })
                }
              />
              <span>Gem løbende som RTF</span>
            </label>
          </section>

          {onChangeSceneNumberingMode ? (
            <section>
              <h3>Scener</h3>
              <div className="sm-setting sm-setting-segment">
                <span className="sm-setting-segment-label">
                  Scenenumre for denne episode
                </span>
                <div
                  className="sm-segmented"
                  role="radiogroup"
                  aria-label="Scenenumre for episoden"
                >
                  {(
                    [
                      ["sequential", "Fortløbende"],
                      ["custom", "Brugerdefinerede"],
                    ] as const
                  ).map(([value, label]) => {
                    const active =
                      (sceneNumberingMode === "custom"
                        ? "custom"
                        : "sequential") === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        disabled={readOnly}
                        className={active ? "is-active" : undefined}
                        onClick={() => onChangeSceneNumberingMode(value)}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <p className="sm-setting-hint">
                Fortløbende omnummererer ved flyt. Retter du et scenenummer,
                skifter episoden til brugerdefinerede, og numrene følger
                scenen — også når du sletter en anden scene. Brugerdefinerede
                numre kan indeholde bogstaver, punktummer og skråstreg (fx 12a,
                2.1 eller 3 / 4).
              </p>
            </section>
          ) : null}

          <section>
            <h3>Episoder</h3>
            <label className="sm-setting">
              <input
                type="checkbox"
                checked={settings.showEpisodeNumbers}
                onChange={(e) =>
                  onSettingsChange({
                    showEpisodeNumbers: e.target.checked,
                  })
                }
              />
              <span>Vis episodenumre</span>
            </label>
            <p className="sm-setting-hint">
              Viser «Episode X: » foran episodetitlen dér, den fremgår.
            </p>
            {onChangeEpisodeNumberingMode ? (
              <>
                <div className="sm-setting sm-setting-segment">
                  <span className="sm-setting-segment-label">
                    Episodenumre for denne produktion
                  </span>
                  <div
                    className="sm-segmented"
                    role="radiogroup"
                    aria-label="Episodenumre for produktionen"
                  >
                    {(
                      [
                        ["sequential", "Fortløbende"],
                        ["custom", "Brugerdefinerede"],
                      ] as const
                    ).map(([value, label]) => {
                      const active =
                        (episodeNumberingMode === "custom"
                          ? "custom"
                          : "sequential") === value;
                      return (
                        <button
                          key={value}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          disabled={readOnly}
                          className={active ? "is-active" : undefined}
                          onClick={() => onChangeEpisodeNumberingMode(value)}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <p className="sm-setting-hint">
                  Fortløbende omnummererer ved tilføj og slet. Retter du et
                  episodenummer, skifter produktionen til brugerdefinerede.
                  Brugerdefinerede numre kan indeholde bogstaver og punktummer
                  (fx 12a eller 2.1).
                </p>
              </>
            ) : null}
          </section>

          {onSetUseSeasons ? (
            <section>
              <h3>Produktion</h3>
              <label className="sm-setting">
                <input
                  type="checkbox"
                  checked={Boolean(useSeasons)}
                  disabled={readOnly}
                  onChange={(e) => onSetUseSeasons(e.target.checked)}
                />
                <span>Brug sæsoner</span>
              </label>
            </section>
          ) : null}

          <div className="sm-user-prefs-actions">
            {onSaveUserPreferences ? (
              <button
                type="button"
                className="sm-user-prefs-save"
                onClick={() => {
                  onSaveUserPreferences();
                  setSavedFlash(true);
                }}
              >
                {savedFlash ? "Gemt" : "Gem mine valg"}
              </button>
            ) : null}
            {onResetUserPreferences ? (
              <button
                type="button"
                className="sm-user-prefs-reset"
                onClick={() => {
                  const ok = window.confirm(
                    "Nulstil alle dine indstillinger til standard?",
                  );
                  if (ok) onResetUserPreferences();
                }}
              >
                Nulstil
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
